import { CVAnalysisTable } from "components/table";
import styles from "./styles.module.scss";
import { useState, useEffect, useRef } from "react";
import { EmptyTable } from "components/empty-table";
import { emptyReport2, SearchIconTwo } from "assets";
import { Button } from "components/button";
import { Input } from "components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Routes } from "router";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Spinner } from "components/loader";
import { useCVAnalyses, CVAnalysisItem } from "hooks";

interface CVAnalysisTableItem {
  id: string;
  cvAnalysisId?: string;
  role: string;
  date: string;
  numberOfCVs: number;
  status: "Completed" | "Drafts";
}

const searchSchema = Yup.object().shape({
  search: Yup.string().trim(),
});

type FilterStatus = "all" | "completed" | "drafts";

const CVAnalysisTableSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get filter, search, and page from URL or use defaults
  const filterFromUrl = (searchParams.get("cvFilter") as FilterStatus) || "all";
  const searchFromUrl = searchParams.get("cvSearch") || "";
  const pageFromUrl = parseInt(searchParams.get("cvPage") || "1", 10);

  const { register, formState: { errors }, watch } = useForm({
    resolver: yupResolver(searchSchema),
    mode: "onBlur",
    defaultValues: {
      search: searchFromUrl,
    },
  });
  const watchSearch = watch("search");

  const [currentFilter, setCurrentFilter] = useState<FilterStatus>(filterFromUrl);
  const [searchTerm, setSearchTerm] = useState<string>(searchFromUrl);
  const [currentPage, setCurrentPage] = useState<number>(pageFromUrl);
  const pageSize = 8;

  // Use the CV analyses hook
  const { cvAnalyses, isLoading } = useCVAnalyses({
    search: searchTerm
  });

  // Track if component has initialized to avoid overwriting URL on mount
  const hasInitialized = useRef(false);

  // Set initialization flag after mount
  useEffect(() => {
    hasInitialized.current = true;
  }, []);

  // Sync URL params with current state (skip on initial mount)
  useEffect(() => {
    if (!hasInitialized.current) return;

    const params = new URLSearchParams(searchParams);
    params.set("tab", "cv-analysis");
    params.set("cvFilter", currentFilter);
    params.set("cvPage", currentPage.toString());
    if (searchTerm) {
      params.set("cvSearch", searchTerm);
    } else {
      params.delete("cvSearch");
    }
    setSearchParams(params, { replace: true });
  }, [currentFilter, searchTerm, currentPage, setSearchParams, searchParams]);

  // Transform API data to component format
  const transformedData: CVAnalysisTableItem[] = cvAnalyses.map((item: CVAnalysisItem) => ({
    id: item.id.toString(),
    cvAnalysisId: item.id.toString(),
    role: item.status !== 'draft' ? item.role.replace(/ - CV Analysis$/i, '') : item.role,
    date: new Date(item.created_at).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }),
    numberOfCVs: item.number_of_cvs,
    status: item.status === 'completed' ? 'Completed' : 'Drafts',
  }));

  // Filter data based on current filter
  const filteredData = transformedData.filter((item) => {
    const matchesFilter = currentFilter === "all" ||
      (currentFilter === "completed" && item.status === "Completed") ||
      (currentFilter === "drafts" && item.status === "Drafts");
    return matchesFilter;
  });

  // Get counts for each status
  const getStatusCount = (status: FilterStatus): number => {
    if (status === "all") {
      return transformedData.length;
    }
    if (status === "completed") {
      return transformedData.filter(item => item.status === "Completed").length;
    }
    if (status === "drafts") {
      return transformedData.filter(item => item.status === "Drafts").length;
    }
    return 0;
  };

  const handleFilter = (status: FilterStatus) => {
    setCurrentFilter(status);
    setCurrentPage(1);
  };

  // Watch for search input changes and update search term with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(watchSearch?.trim() || "");
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [watchSearch]);

  // Calculate pagination for filtered data
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className='lg:px-5 px-0 py-10'>
      <h3 className={styles.headerText}>
        CV Analysis Jobs
      </h3>
      <div className="w-full overflow-x-auto">
        <div className={styles.filterSection}>
          <div className={styles.buttons}>
            <Button
              type="transparent"
              onClick={() => handleFilter("all")}
              className={`${styles.filterButton} ${currentFilter === "all" ? styles.active : ""}`}
            >
              All <span className={styles.number}>{getStatusCount("all")}</span>
            </Button>
            <Button
              type="transparent"
              onClick={() => handleFilter("completed")}
              className={`${styles.filterButton} ${currentFilter === "completed" ? styles.active : ""}`}
            >
              Completed <span className={styles.number}>{getStatusCount("completed")}</span>
            </Button>
            <Button
              type="transparent"
              onClick={() => handleFilter("drafts")}
              className={`${styles.filterButton} ${currentFilter === "drafts" ? styles.active : ""}`}
            >
              Draft <span className={styles.number}>{getStatusCount("drafts")}</span>
            </Button>
          </div>

          <Input
            Icon={SearchIconTwo}
            iconClassName={styles.searchIcon}
            placeholder="Search"
            type="search"
            name="search"
            register={register}
            validatorMessage={errors.search?.message || ''}
            showRequired={false}
            parentClassName={`ml-3 !rounded-[4px] min-w-[120px] ${styles.searchInputWrapper}`}
            className={`!h-[39px] ${styles.searchInput}`}
          />
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-10">
            <Spinner />
          </div>
        ) : (
          <CVAnalysisTable
            tableBodyItems={paginatedData}
            handleView={(id: string, cvAnalysisId?: string) => {
              const item = transformedData.find(i => i.id === id || i.cvAnalysisId === (cvAnalysisId ?? id));
              if (item?.status === 'Drafts') {
                navigate(`${Routes.publishAssessment}?id2=${id}`);
              } else {
                const currentUrl = location.pathname + location.search;
                navigate(
                  `${Routes.reviewTalentsCVAnalysis(cvAnalysisId || id)}?retCVAnalysis=${encodeURIComponent(currentUrl)}`
                );
              }
            }}
            pagination={{
              current: currentPage,
              total: totalPages,
              handleChange: (page: number) => setCurrentPage(page),
              count: filteredData.length,
              length: pageSize,
            }}
            emptyState={
              paginatedData.length === 0 ? (
                <EmptyTable
                  image={emptyReport2}
                  text={
                    currentFilter === "all"
                      ? "No CV analysis jobs found"
                      : currentFilter === "completed"
                      ? "No completed CV analysis jobs found"
                      : "No draft CV analysis jobs found"
                  }
                />
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
};

export { CVAnalysisTableSection };
